"use client"

import type { ReactNode } from "react"
import { ChevronLeft } from "lucide-react"

import { TextAction } from "@/components/graphite/text-action"
import { cn } from "@/lib/utils"

/**
 * The bar at the top of a takeover: a way back, what you are looking at, the
 * facts about it, and what you can do to it.
 *
 * Every screen that takes the record over drew its own — the task list, the
 * task composer, the comment list, a comment on narrow, the profile editor —
 * and they were the same four class strings copied five times, down to the
 * `-mx-1` that pulls the chevron's touch target back into the padding. Which is
 * how they had already drifted: two compacted their controls above @2xl and
 * three did not, so the same bar was 44px tall on one screen and 32px on the
 * next at the same width.
 */
export function OverlayNav({
  onBack,
  /** What the chevron announces — "Back to the record", "Back to all tasks". */
  backLabel,
  title,
  /**
   * Machine facts between the title and the actions: "12 open", "3/40". Mono,
   * by convention, and the caller writes them because only it knows the shape.
   */
  meta,
  actions,
  /**
   * `responsive` compacts the controls above @2xl, for a bar that is on screen
   * at every width. `comfortable` keeps the 44px touch targets, for one that is
   * only ever drawn on narrow — where compacting it would be shrinking a target
   * meant for a thumb.
   */
  size = "responsive",
  className,
}: {
  onBack: () => void
  backLabel: string
  title: string
  meta?: ReactNode
  actions?: ReactNode
  size?: "responsive" | "comfortable"
  className?: string
}) {
  const compact = size === "responsive"

  return (
    <div
      className={cn(
        "-mx-1 mb-3 flex flex-none items-center gap-2 border-b pb-3",
        className,
      )}
    >
      <button
        type="button"
        onClick={onBack}
        aria-label={backLabel}
        className={cn(
          "flex h-11 w-11 flex-none items-center justify-center text-muted-foreground transition-colors hover:text-foreground",
          compact && "@2xl:h-8 @2xl:w-8",
        )}
      >
        <ChevronLeft className={cn("h-5 w-5", compact && "@2xl:h-4 @2xl:w-4")} />
      </button>

      <span
        className={cn(
          "min-w-0 flex-1 truncate text-[15px] font-semibold leading-[1.2]",
          compact && "@2xl:text-[13px]",
        )}
      >
        {title}
      </span>

      {meta}
      {actions}
    </div>
  )
}

/**
 * An action in that bar, sized to sit in it — a thumb-sized target on narrow
 * that tightens with the rest of the bar. The sizing was a class string copied
 * beside every copy of the bar.
 */
export function OverlayNavAction({
  size = "responsive",
  className,
  ...props
}: React.ComponentProps<typeof TextAction> & {
  size?: "responsive" | "comfortable"
}) {
  return (
    <TextAction
      className={cn(
        "h-11 px-4 text-[13px]",
        size === "responsive" && "@2xl:h-8 @2xl:px-3 @2xl:text-[12px]",
        className,
      )}
      {...props}
    />
  )
}
